import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { formatDayKey } from "../../utils/calendarDay";
import { formatTime } from "../../utils/tripDisplay";
import { formatMinutes, listNames, percentUnderPosted, type TripRecap as Recap } from "../../utils/tripRecap";
import "./TripStory.css";

/** One recap card: what it says, and the colour it says it in. */
interface RecapCard {
  key: string;
  tone: "sea" | "lagoon" | "reef" | "coral" | "sand" | "plum";
  body: ReactNode;
}

const weekday = (dayKey: string) => formatDayKey(dayKey, { weekday: "long" });
const shortDay = (dayKey: string) => formatDayKey(dayKey, { weekday: "short" });

/**
 * The cards for a trip, in the order they're shown. Each fact appears only
 * when the trip has the data behind it, so a short trip gets a short recap.
 */
function recapCards(recap: Recap, trip: { name: string; dates: string; party: string[] }): RecapCard[] {
  const cards: RecapCard[] = [];
  const total = recap.rides + recap.shows;
  const firstRide = recap.activities.find((a) => (a.kind === "ride" || a.kind === "show") && !a.gaveUp && a.start);

  cards.push({
    key: "cover",
    tone: "sea",
    body: (
      <>
        <span className="rc-k">{trip.dates}</span>
        <div className="rc-title">{trip.name}</div>
        {recap.places.length > 0 && <p className="rc-fine">{listNames(recap.places.slice(0, 4))}</p>}
        {trip.party.length > 0 && (
          <div className="rc-who" aria-label={listNames(trip.party)}>
            {trip.party.map((p) => (
              <span key={p} title={p}>{p.charAt(0).toUpperCase()}</span>
            ))}
          </div>
        )}
        <div className="rc-spacer" />
        {total > 0 ? (
          <>
            <span className="rc-k">In {recap.days.length} {recap.days.length === 1 ? "day" : "days"}</span>
            <div className="rc-huge">{total}</div>
            <p className="rc-say">
              {recap.shows > 0 ? "rides and shows" : total === 1 ? "ride" : "rides"}
              {firstRide?.start && firstRide.dayKey
                ? `, starting with ${firstRide.name} at ${formatTime(firstRide.start, firstRide.zone)} on ${weekday(firstRide.dayKey)}.`
                : "."}
            </p>
          </>
        ) : (
          <>
            <div className="rc-huge">{recap.activities.length}</div>
            <p className="rc-say">things logged in {recap.days.length} {recap.days.length === 1 ? "day" : "days"}.</p>
          </>
        )}
      </>
    ),
  });

  const pv = recap.postedVsWaited;
  if (pv && pv.count >= 3) {
    const pct = percentUnderPosted(pv);
    cards.push({
      key: "lines",
      tone: "lagoon",
      body: (
        <>
          <span className="rc-k">Lines this trip</span>
          <div className="rc-huge">{Math.abs(pct)}%</div>
          <p className="rc-say">
            {pct >= 0 ? "shorter" : "longer"} than the signs said. You waited {formatMinutes(pv.waited)} where they
            posted {formatMinutes(pv.posted)}.
          </p>
          <div className="rc-vs" aria-hidden="true">
            <span>Posted</span><i style={{ width: "100%" }} />
            <span>Waited</span><i className="hot" style={{ width: `${Math.min(100, (pv.waited / pv.posted) * 100)}%` }} />
          </div>
          <div className="rc-spacer" />
          {recap.tiers.length > 0 && (
            <div className="rc-bars">
              {recap.tiers.slice(0, 3).map((t) => (
                <div key={t.name} style={{ ["--w" as string]: `${Math.min(100, (t.waited / t.posted) * 100)}%` }}>
                  <span>{t.name}, {t.count} {t.count === 1 ? "ride" : "rides"}</span>
                  <span>{t.waited} of {t.posted}m</span>
                </div>
              ))}
            </div>
          )}
          <p className="rc-fine">Across {pv.count} rides with a posted wait.</p>
        </>
      ),
    });
  }

  if (recap.topRide) {
    const { name, rides } = recap.topRide;
    const waits = rides.map((r) => r.waited ?? 0);
    const max = Math.max(1, ...waits);
    const longest = rides.reduce((b, r) => ((r.waited ?? 0) > (b.waited ?? 0) ? r : b), rides[0]!);
    cards.push({
      key: "top-ride",
      tone: "reef",
      body: (
        <>
          <span className="rc-k">Your ride of the trip</span>
          <div className="rc-title rc-title-sm">{name}</div>
          <div className="rc-huge">×{rides.length}</div>
          <div className="rc-spacer" />
          {waits.some((w) => w > 0) && (
            <div className="rc-cols" aria-label="Wait for each ride">
              {rides.slice(0, 8).map((r) => (
                <div key={r.id}>
                  <span>{r.waited != null ? `${r.waited}m` : "–"}</span>
                  <b className={r === longest ? "hot" : undefined} style={{ height: `${Math.max(4, ((r.waited ?? 0) / max) * 100)}%` }} />
                  <span>{r.dayKey ? shortDay(r.dayKey) : ""}</span>
                </div>
              ))}
            </div>
          )}
          {longest.waited != null && longest.posted != null && (
            <p className="rc-fine">The {longest.waited}m wait was posted at {longest.posted}.</p>
          )}
        </>
      ),
    });
  }

  const walkOn = recap.bestWalkOn;
  if (walkOn && walkOn.posted! - walkOn.waited! >= 10) {
    cards.push({
      key: "walk-on",
      tone: "plum",
      body: (
        <>
          <span className="rc-k">Most time saved</span>
          <div className="rc-title rc-title-sm">{walkOn.name}</div>
          <div className="rc-huge">{walkOn.waited}<small> min</small></div>
          <p className="rc-say">
            in line where {walkOn.posted} was posted{walkOn.dayKey ? `, on ${weekday(walkOn.dayKey)}` : ""}.
          </p>
        </>
      ),
    });
  }

  const big = recap.bigDay;
  if (big && recap.days.length > 1 && big.rides + big.shows >= 3) {
    cards.push({
      key: "big-day",
      tone: "coral",
      body: (
        <>
          <span className="rc-k">Biggest day · {weekday(big.dayKey)}</span>
          <div className="rc-huge">{big.rides + big.shows}</div>
          <p className="rc-say">
            {big.shows > 0 ? "rides and shows" : "rides"}
            {big.first && big.last ? `, from ${formatTime(big.first, big.zone)} to ${formatTime(big.last, big.zone)}.` : "."}
          </p>
          <div className="rc-spacer" />
          {big.places.length > 0 && <p className="rc-fine">{listNames(big.places)}</p>}
          {big.waited > 0 && <p className="rc-fine">{formatMinutes(big.waited)} in line that day.</p>}
        </>
      ),
    });
  }

  if (recap.gaveUp) {
    const g = recap.gaveUp;
    const later = recap.activities.filter((a) => a.name === g.name && !a.gaveUp && a.kind === g.kind).length;
    cards.push({
      key: "got-away",
      tone: "sand",
      body: (
        <>
          <span className="rc-k">The one that got away</span>
          <div className="rc-title rc-title-sm">{g.name}</div>
          <p className="rc-say">
            You left the line{g.dayKey ? ` on ${weekday(g.dayKey)}` : ""}
            {g.start ? ` at ${formatTime(g.end ?? g.start, g.zone)}` : ""}.
          </p>
          {later > 0 && <p className="rc-say">Then you got on {later === 1 ? "once" : later === 2 ? "twice" : `${later} times`}.</p>}
        </>
      ),
    });
  }

  const longest = recap.longestWait;
  if (longest?.waited != null && longest.waited >= 30 && !recap.topRide?.rides.some((r) => r.id === longest.id)) {
    cards.push({
      key: "longest",
      tone: "sea",
      body: (
        <>
          <span className="rc-k">Longest line</span>
          <div className="rc-title rc-title-sm">{longest.name}</div>
          <div className="rc-huge">{formatMinutes(longest.waited)}</div>
          <p className="rc-say">
            {longest.dayKey ? `on ${weekday(longest.dayKey)}` : ""}
            {longest.posted != null ? `, posted at ${longest.posted}.` : "."}
          </p>
        </>
      ),
    });
  }

  if (recap.earliest) {
    const e = recap.earliest;
    cards.push({
      key: "earliest",
      tone: "plum",
      body: (
        <>
          <span className="rc-k">Early start</span>
          <div className="rc-huge">{formatTime(e.at, e.zone).replace(/\s?[AP]M$/i, "")}</div>
          <p className="rc-say">
            {e.name} on {weekday(e.dayKey)}, the earliest start of the trip.
          </p>
        </>
      ),
    });
  }

  if (recap.food >= 2) {
    const stops = new Map<string, number>();
    for (const a of recap.activities) if (a.kind === "food") stops.set(a.name, (stops.get(a.name) ?? 0) + 1);
    cards.push({
      key: "food",
      tone: "sand",
      body: (
        <>
          <span className="rc-k">Fuel</span>
          <div className="rc-huge">{recap.food}</div>
          <p className="rc-say">
            food and drink stops{recap.topFood ? `. ${recap.topFood.name} ${recap.topFood.count === 2 ? "twice" : `${recap.topFood.count} times`}.` : "."}
          </p>
          <div className="rc-spacer" />
          <div className="rc-list">
            {[...stops.entries()]
              .sort((a, b) => b[1] - a[1])
              .slice(0, 4)
              .map(([name, count]) => (
                <div key={name}><span>{name}</span><small>{count > 1 ? `×${count}` : ""}</small></div>
              ))}
          </div>
        </>
      ),
    });
  }

  const rows: [string, string][] = [];
  if (recap.rides > 0) rows.push(["Rides", String(recap.rides)]);
  if (recap.shows > 0) rows.push(["Shows", String(recap.shows)]);
  if (recap.waited > 0) rows.push(["Time in line", formatMinutes(recap.waited)]);
  if (recap.places.length > 0) rows.push(["Parks and events", String(recap.places.length)]);
  if (recap.food > 0) rows.push(["Food and drink stops", String(recap.food)]);
  if (rows.length > 0) {
    cards.push({
      key: "summary",
      tone: "sea",
      body: (
        <>
          <span className="rc-k">That's the trip</span>
          <div className="rc-title">{trip.name}</div>
          <div className="rc-list">
            {rows.map(([k, v]) => (
              <div key={k}><span>{k}</span><small>{v}</small></div>
            ))}
          </div>
        </>
      ),
    });
  }
  return cards;
}

interface TripRecapProps {
  recap: Recap;
  trip: { name: string; dates: string; party: string[] };
  onClose: () => void;
}

/** The recap: one fact per card, tapped through like stories. */
export function TripRecap({ recap, trip, onClose }: TripRecapProps) {
  const cards = useMemo(() => recapCards(recap, trip), [recap, trip]);
  const [index, setIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const go = useCallback((n: number) => setIndex(Math.max(0, Math.min(cards.length - 1, n))), [cards.length]);

  useEffect(() => {
    ref.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const card = cards[index]!;
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowRight" || e.key === " ") {
      e.preventDefault();
      go(index + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(index - 1);
    }
  };
  const onTap = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (e.clientX - r.left < r.width * 0.33) go(index - 1);
    else if (index === cards.length - 1) onClose();
    else go(index + 1);
  };

  return (
    <div
      className="trip-recap"
      data-tone={card.tone}
      role="dialog"
      aria-modal="true"
      aria-label={`${trip.name} recap`}
      tabIndex={-1}
      ref={ref}
      onKeyDown={onKey}
    >
      <div className="rc-prog" aria-hidden="true">
        {cards.map((c, k) => (
          <i key={c.key} className={k <= index ? "d" : undefined} />
        ))}
      </div>
      <div className="rc-top">
        <span>{trip.name}</span>
        <button type="button" className="rc-close" onClick={onClose} aria-label="Close recap">
          ✕
        </button>
      </div>
      <div
        className="rc-card"
        key={card.key}
        onClick={onTap}
        role="button"
        tabIndex={-1}
        aria-label={`Card ${index + 1} of ${cards.length}. Tap the right side for the next card, the left for the one before.`}
      >
        {card.body}
      </div>
      <div className="rc-foot">
        <button type="button" className="rc-btn ghost" onClick={() => go(index - 1)} disabled={index === 0}>
          Back
        </button>
        <span className="rc-count">{index + 1} / {cards.length}</span>
        <button
          type="button"
          className="rc-btn"
          onClick={() => (index === cards.length - 1 ? onClose() : go(index + 1))}
        >
          {index === cards.length - 1 ? "Done" : "Next"}
        </button>
      </div>
    </div>
  );
}
